import { useLocale as __useLocale } from "@/context/LocaleContext";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getProducts, getCollections, getCampaigns, postSubscriber, formatPrice } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { canPurchase } from "@/lib/inventory";
import { ProductCard } from "@/components/ProductCard";
import { Reveal, ArchReveal } from "@/components/motion";
import { IMG, FALLBACK_IMG } from "@/lib/images";
import { toast } from "sonner";
import { ArrowRight, Gift, Lock, Sparkles, Truck } from "lucide-react";

export function WeeklyPicks() {
  const { t: __t } = __useLocale();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true), [failed, setFailed] = useState(false), [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true); setFailed(false);
    getProducts({ tag: "weekly", limit: 6 }).then(items => { if (active) setProducts(items); })
      .catch(() => { if (active) { setProducts([]); setFailed(true); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [retry]);

  return (
    <section data-testid="weekly-picks-section" className="py-16 md:py-24 bg-shule-paper border-y border-shule-line">
      <div className="container-shule">
        <Reveal className="mb-10 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Bu Haftanın Shule Seçkisi")}</h2>
          <p className="mt-2 text-sm text-shule-soft">{__t("Senin için özenle seçilen favoriler")}</p>
        </Reveal>
        {loading ? <p role="status" className="text-center text-shule-soft">{__t("Yükleniyor…")}</p> : failed ?
          <div role="alert" className="text-center"><p>{__t("Ürünler şu anda yüklenemedi.")}</p>
            <p className="mt-2 text-shule-soft">{__t("Ürünlerin tükenmiş olduğu anlamına gelmez. Yeniden deneyebilirsiniz.")}</p>
            <button className="btn-outline-shule mt-4" onClick={() => setRetry(value => value + 1)}>{__t("Yeniden Dene")}</button>
          </div> : products.length === 0 ? <p role="status" className="text-center text-shule-soft">{__t("Haftalık seçkide henüz ürün bulunmuyor.")}</p> :
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
            {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>}
        <Reveal className="mt-10 text-center">
          <Link to="/kategori/tumu" data-testid="weekly-view-all" className="btn-outline-shule">{__t("Tüm Ürünleri Gör ")}<ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

const BANNER_IMGS = {
  "kombinini-tamamla": "https://images.pexels.com/photos/30415591/pexels-photo-30415591.jpeg?auto=compress&cs=tinysrgb&w=2000",
  "hediye-zamani": "/assets/img/banner-hediye.jpeg",
};

export function CampaignBanners() {
  const { t: __t } = __useLocale();
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    getCampaigns().then((c) => setCampaigns(c.filter((x) => x.active !== false))).catch(() => {});
  }, []);

  const picks = ["kombinini-tamamla", "hediye-zamani"]
    .map((s) => campaigns.find((c) => c.slug === s))
    .filter(Boolean);

  if (picks.length === 0) return null;

  return (
    <section data-testid="campaign-banners-section" className="py-14 md:py-20">
      <div className="container-shule grid md:grid-cols-2 gap-5">
        {picks.map((c, i) => (
          <Reveal key={c.slug} delay={i * 0.1}>
            <Link to={c.cta_link || "/"} data-testid={`campaign-banner-${c.slug}`}
              className="group relative block rounded-[2rem] overflow-hidden border border-shule-line min-h-[280px]">
              <img src={BANNER_IMGS[c.slug] || IMG.jewel_flat || IMG.hero_secondary} alt={__t("")} loading="lazy"
                onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              <div className="absolute inset-0 bg-gradient-to-r from-shule-coffee/60 via-shule-coffee/25 to-transparent" />
              <div className="relative p-8 lg:p-10 flex flex-col justify-center min-h-[280px]">
                <h3 className="font-serif text-3xl lg:text-4xl text-shule-paper">{__t(c.title)}</h3>
                <p className="mt-2 text-sm text-shule-paper/85 max-w-[240px]">{__t(c.subtitle)}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 self-start rounded-full bg-shule-paper text-shule-coffee text-sm font-semibold px-6 min-h-[44px] transition-colors group-hover:bg-shule-goldlight">
                  {__t(c.cta_label)} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function BundleBuilder() {
  const { t: __t } = __useLocale();
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState([]);
  const { addToCart } = useStore();

  useEffect(() => {
    getProducts({ tag: "bundle", limit: 6 }).then((r) => {
      const purchasable = r.filter(p => canPurchase(p) && p.checkout_provider !== "shopify");
      setCandidates(purchasable);
      setSelected(purchasable.slice(0, 3).map((p) => p.slug));
    }).catch(() => {});
  }, []);

  const selectedProducts = useMemo(
    () => candidates.filter((p) => selected.includes(p.slug)),
    [candidates, selected]
  );
  const total = selectedProducts.reduce((s, p) => s + p.price, 0);
  const saving = selected.length >= 3 ? Math.round(total * 0.25) : 0;

  const toggle = (slug) => {
    setSelected((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= 3) {
        toast.info(__t("Kombin en fazla 3 parçadan oluşur"));
        return prev;
      }
      return [...prev, slug];
    });
  };

  const addAll = () => {
    selectedProducts.forEach((p) => addToCart(p, p.variants?.[0], 1, false, true));
    toast.success(__t("Tüm kombin sepete eklendi"));
  };

  return (
    <section data-testid="bundle-builder-section" id="kombin" className="py-16 md:py-24 bg-shule-cream/60">
      <div className="container-shule grid lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <p className="eyebrow">{__t("Kombinini Tamamla")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee text-balance">{__t("Kendi üçlünü kur, anına göre kombinle")}</h2>
          <p className="mt-4 text-shule-mocha leading-relaxed max-w-md">{__t("Kolye, küpe ve bilekliğini yan yana dene. Üç parçalı kombinlerde %25 ayrıcalık otomatik uygulanır.")}</p>
          <div className="mt-8 rounded-3xl border border-shule-line bg-shule-paper p-6 shadow-[0_10px_30px_-10px_rgba(38,33,31,0.05)]">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-shule-mocha">{__t(selected.length)}{__t("/3 parça seçili")}</span>
              {saving > 0 && <span className="badge-tag !bg-shule-goldlight/80">{__t("Kombin ayrıcalığı −")}{formatPrice(saving)}</span>}
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span data-testid="bundle-total-price" className="font-serif text-4xl">{formatPrice(total - saving)}</span>
              {saving > 0 && <span className="text-shule-soft line-through">{formatPrice(total)}</span>}
            </div>
            <button data-testid="bundle-add-all-button" onClick={addAll} disabled={selected.length === 0}
              className="btn-primary-shule w-full mt-5 disabled:opacity-40">{__t("Tüm Kombini Sepete Ekle")}</button>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {candidates.map((p, i) => {
            const active = selected.includes(p.slug);
            return (
              <Reveal key={p.slug} delay={i * 0.06}>
                <button data-testid={`bundle-candidate-${p.slug}`} onClick={() => toggle(p.slug)}
                  className={`w-full text-left rounded-3xl border p-2 transition-all duration-300 ${active ? "border-shule-gold bg-shule-paper shadow-[0_14px_30px_-12px_rgba(185,155,105,0.35)]" : "border-shule-line bg-shule-paper/60 hover:border-shule-lineaccent"}`}>
                  <div className="rounded-2xl overflow-hidden">
                    <img src={p.images?.[0]} alt={__t(p.name)} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                      className="w-full aspect-square object-cover" />
                  </div>
                  <p className="mt-2 px-1 font-serif text-base leading-tight">{__t(p.name)}</p>
                  <p className="px-1 pb-1 text-xs font-bold text-shule-mocha">{formatPrice(p.price)}</p>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const WHO = [
  { id: "arkadas", label: "Arkadaşa" },
  { id: "partner", label: "Sevgiliye" },
  { id: "anne", label: "Anneye" },
  { id: "kendim", label: "Kendine" },
];
const MOMENTS = [
  { id: "dogum-gunu", label: "Doğum Günü" },
  { id: "yildonumu", label: "Yıldönümü" },
  { id: "yeni-is", label: "Yeni İş" },
  { id: "sadece", label: "Sadece" },
];
const BUDGETS = [
  { id: "600", label: "600 TL altı" },
  { id: "1200", label: "600 – 1.200 TL" },
  { id: "9999", label: "1.200 TL üzeri" },
];

export function GiftTeaser() {
  const { t: __t } = __useLocale();
  const [who, setWho] = useState(null);
  const [moment, setMoment] = useState(null);
  const [budget, setBudget] = useState(null);
  const navigate = useNavigate();
  const ready = who && moment && budget;

  return (
    <section data-testid="gift-finder-teaser-section" className="py-16 md:py-24">
      <div className="container-shule">
        <Reveal className="max-w-2xl mx-auto text-center">
          <p className="eyebrow">{__t("Hediye Zamanı")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Üç soruda doğru hediye")}</h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-10 max-w-3xl mx-auto rounded-[2rem] border border-shule-line bg-shule-paper p-6 sm:p-10 shadow-[0_10px_30px_-10px_rgba(38,33,31,0.05)]">
          {[
            { step: 1, q: "Kime göre?", opts: WHO, val: who, set: setWho, tid: "who" },
            { step: 2, q: "Hangi an için?", opts: MOMENTS, val: moment, set: setMoment, tid: "moment" },
            { step: 3, q: "Bütçen ne?", opts: BUDGETS, val: budget, set: setBudget, tid: "budget" },
          ].map(({ step, q, opts, val, set, tid }) => (
            <div key={step} className="mb-7 last:mb-0">
              <p className="text-sm font-semibold text-shule-mocha mb-3">
                <span className="font-serif italic text-shule-goldhover text-lg mr-2">0{__t(step)}</span>{__t(q)}
              </p>
              <div className="flex flex-wrap gap-2">
                {opts.map((o) => (
                  <button key={o.id} data-testid={`gift-${tid}-${o.id}`} onClick={() => set(o.id)}
                    className={`min-h-[44px] px-5 rounded-full border text-sm font-medium transition-colors duration-300 ${val === o.id ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent hover:border-shule-gold"}`}>
                    {__t(o.label)}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button
            data-testid="gift-teaser-submit"
            disabled={!ready}
            onClick={() => navigate(`/hediye?who=${who}&moment=${moment}&budget=${budget}`)}
            className="btn-primary-shule w-full mt-8 disabled:opacity-40"
          >{__t("Önerileri Gör ")}<ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </div>
    </section>
  );
}

const MOMENT_TILES = [
  { title: "Günlük", desc: "Her güne eşlik eden parçalar", img: IMG.model_shirt, link: "/kategori/tumu?occasion=gunluk" },
  { title: "Ofis", desc: "Sade ve güçlü dokunuşlar", img: IMG.ugc1, link: "/kategori/tumu?occasion=ofis" },
  { title: "Akşam", desc: "Işıltını öne çıkar", img: IMG.model_dress, link: "/kategori/tumu?occasion=aksam" },
  { title: "Özel Gün", desc: "Anına özel seçimler", img: IMG.ring_hand, link: "/kategori/tumu?occasion=ozel-gun" },
  { title: "Hediye", desc: "Kime ve ana göre bul", img: IMG.ring_box, link: "/hediye" },
];

export function LifeMoments() {
  const { t: __t } = __useLocale();
  return (
    <section data-testid="life-moments-section" className="py-16 md:py-24 bg-shule-paper border-y border-shule-line">
      <div className="container-shule">
        <Reveal className="mb-10 text-center">
          <p className="eyebrow">{__t("Sana Göre Keşfet")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Her Anında Sen")}</h2>
          <p className="mt-2 text-sm text-shule-soft">{__t("Bugünkü anını seç, sana uygun parçaları keşfet.")}</p>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
          {MOMENT_TILES.map((m, i) => (
            <ArchReveal key={m.title} delay={i * 0.08}>
              <Link data-testid={`moment-tile-${i}`} to={m.link} className="group block relative">
                <div className="arch-card">
                  <div className="relative aspect-[3/4]">
                    <img src={m.img} alt={__t(m.title)} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-shule-coffee/55 via-transparent to-transparent" />
                    <div className="absolute bottom-0 p-4">
                      <p className="font-serif text-xl text-shule-paper">{__t(m.title)}</p>
                      <p className="text-xs text-shule-paper/85 mt-0.5">{__t(m.desc)}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </ArchReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export const STYLE_SET_IMAGES = {
  "gun-isigi": "https://images.pexels.com/photos/1927259/pexels-photo-1927259.jpeg?auto=compress&cs=tinysrgb&w=3840",
  "gece-isiltisi": "https://images.pexels.com/photos/1457801/pexels-photo-1457801.jpeg?auto=compress&cs=tinysrgb&w=3840",
  "hafta-sonu-rahatligi": "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?auto=format&fit=crop&w=2000&q=88",
};

export function StyleSets() {
  const { t: __t } = __useLocale();
  const [collections, setCollections] = useState([]);
  useEffect(() => {
    getCollections().then(setCollections).catch(() => {});
  }, []);

  return (
    <section data-testid="style-sets-section" className="py-16 md:py-24">
      <div className="container-shule">
        <Reveal className="mb-10">
          <p className="eyebrow">{__t("Shule Style Sets")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Hazır kombinler, editoryal hikâyeler")}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {collections.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.1}>
              <Link data-testid={`style-set-${c.slug}`} to={`/koleksiyon/${c.slug}`}
                className="group block rounded-[2rem] border border-shule-line bg-shule-paper overflow-hidden hover:shadow-[0_24px_50px_-20px_rgba(38,33,31,0.22)] transition-shadow duration-500">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={STYLE_SET_IMAGES[c.slug] || c.hero_image} alt={__t(c.name)} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                  <span className="absolute top-4 left-4 badge-tag !bg-shule-paper/90">{__t(c.chapter)}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl">{__t(c.name)}</h3>
                  <p className="text-sm text-shule-mocha mt-2 leading-relaxed">{__t(c.story)}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-shule-goldhover">{__t("Seti Keşfet ")}<ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const TIERS = [
  { name: "Silver", perk: "Doğum günü sürprizi, erken erişim", points: "Her 10 TL = 1 puan" },
  { name: "Gold", perk: "Ücretsiz kargo, özel davetler", points: "Her 10 TL = 1.5 puan" },
  { name: "Velvet", perk: "Kişisel stil danışmanı, atölye buluşmaları", points: "Her 10 TL = 2 puan" },
];

export function ClubSection() {
  const { t: __t } = __useLocale();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await postSubscriber({ email, source: "club" });
      toast.success(__t(r.message));
      setEmail("");
    } catch (err) {
      toast.error(__t(err.response?.data?.detail || "Bir sorun oluştu"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="club-section" className="py-16 md:py-24 bg-shule-pudra/40 border-y border-shule-line">
      <div className="container-shule grid lg:grid-cols-2 gap-10 items-center">
        <Reveal>
          <p className="eyebrow">{__t("Shule Club")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee text-balance">{__t("Erken erişim, doğum günü avantajları, özel setler")}</h2>
          <p className="mt-4 text-shule-mocha leading-relaxed max-w-md">{__t("Her alışverişte puan kazan, doğum gününde sürprizle karşılaş, yeni koleksiyonlara herkesten önce ulaş.")}</p>
          <form data-testid="club-signup-form" onSubmit={submit} className="mt-8 flex max-w-md rounded-full border border-shule-lineaccent bg-shule-paper overflow-hidden focus-within:border-shule-gold transition-colors">
            <input data-testid="club-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={__t("E-posta adresin")} className="flex-1 min-w-0 bg-transparent px-5 text-sm outline-none min-h-[52px]" />
            <button data-testid="club-join-button" disabled={loading} className="px-6 bg-shule-coffee text-shule-paper text-sm font-semibold hover:bg-shule-goldhover transition-colors">{__t("Katıl")}</button>
          </form>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div data-testid={`club-tier-${t.name.toLowerCase()}`}
                className={`arch-card p-6 pt-10 text-center h-full ${i === 1 ? "bg-shule-goldlight/40 border-shule-gold/40" : ""}`}>
                <Sparkles className="w-5 h-5 mx-auto text-shule-gold" />
                <p className="mt-3 font-serif text-2xl">{__t(t.name)}</p>
                <p className="mt-2 text-xs text-shule-mocha leading-relaxed">{__t(t.perk)}</p>
                <p className="mt-3 text-[11px] uppercase tracking-wider text-shule-soft">{__t(t.points)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function StoresSection() {
  const { t: __t } = __useLocale();
  return (
    <section data-testid="stores-section" className="py-16 md:py-24">
      <div className="container-shule">
        <Reveal className="mb-10 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Mağazalarımız & Kiosklar")}</h2>
          <p className="mt-2 text-sm text-shule-soft">{__t("Shule deneyimini sana en yakın noktada yaşa")}</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-5">
          {[
            {
              img: "/assets/img/loc-store-1.jpeg", title: "Mağazalarımız",
              desc: "Kemerli nişler, sıcak ışık, kişisel stil danışmanı.",
              cta: "Tüm Mağazaları Gör", link: "/magazalar", testid: "stores-view-all-link",
            },
            {
              img: "/assets/img/kiosk-hero.jpeg", title: "AVM Kioskları",
              desc: "3 × 3 m modüler format — şimdi daha yakınındayız.",
              cta: "Kioskları Keşfet", link: "/kiosklar", testid: "kiosks-view-all-link",
            },
          ].map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <Link to={s.link} data-testid={s.testid}
                className="group relative block rounded-[2rem] overflow-hidden border border-shule-line min-h-[320px]">
                <img src={s.img} alt={__t(s.title)} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
                <div className="absolute inset-0 bg-gradient-to-t from-shule-coffee/65 via-shule-coffee/15 to-transparent" />
                <div className="relative p-8 flex flex-col justify-end min-h-[320px]">
                  <h3 className="font-serif text-3xl text-shule-paper">{__t(s.title)}</h3>
                  <p className="mt-1.5 text-sm text-shule-paper/85 max-w-xs">{__t(s.desc)}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 self-start rounded-full bg-shule-paper text-shule-coffee text-sm font-semibold px-6 min-h-[44px] transition-colors group-hover:bg-shule-goldlight">
                    {__t(s.cta)} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const UGC = [IMG.ugc1, IMG.ugc2, IMG.ugc3, IMG.ugc4, IMG.ugc5, IMG.ugc6];

export function UgcGrid() {
  const { t: __t } = __useLocale();
  return (
    <section data-testid="ugc-section" className="py-16 md:py-24 bg-shule-cream/60 border-y border-shule-line">
      <div className="container-shule">
        <Reveal className="text-center mb-10">
          <p className="eyebrow">{__t("#ShuleHerAnımda")}</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Topluluğumuzun anları")}</h2>
          <p className="mt-3 text-sm text-shule-soft">{__t("Haftalık reels serisi ve kombin önerileri için bizi takip et.")}</p>
        </Reveal>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
          {UGC.map((u, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div data-testid={`ugc-tile-${i}`} className={`overflow-hidden border border-shule-line ${i % 2 === 0 ? "rounded-t-[60px] rounded-b-2xl" : "rounded-2xl"}`}>
                <img src={u} alt={__t(`Shule topluluk fotoğrafı ${i + 1}`)} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  className="w-full aspect-square object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CorporateBand() {
  const { t: __t } = __useLocale();
  return (
    <section data-testid="corporate-band" className="py-16 md:py-24">
      <div className="container-shule">
        <Reveal>
          <div className="relative rounded-[2rem] overflow-hidden border border-shule-line min-h-[300px]">
            <img src="/assets/img/loc-store-2.jpeg" alt={__t("Shule mağaza içi")} loading="lazy"
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-shule-base via-shule-base/85 to-shule-base/20" />
            <div className="relative p-8 lg:p-14 max-w-lg">
              <p className="eyebrow">{__t("Kurumsal")}</p>
              <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">{__t("Shule ile Büyüyelim")}</h2>
              <p className="mt-3 text-shule-mocha leading-relaxed">{__t("Güçlü marka, kârlı yatırım, estetik tasarım. Mağaza ve kiosk formatlarıyla kendi şehrinde Shule aç.")}</p>
              <Link to="/bayilik" data-testid="corporate-franchise-cta" className="btn-primary-shule mt-6">{__t("Bayilik Bilgisi Al ")}<ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const TRUST = [
  { icon: Truck, title: "Ücretsiz Kargo", desc: "750 TL ve üzeri" },
  { icon: Lock, title: "Güvenli Ödeme", desc: "Güvenli ödeme ekranı" },
  { icon: Gift, title: "Hediye Seçimi", desc: "Hediye seçeneklerini keşfet" },
  { icon: Sparkles, title: "Her Anında Sen", desc: "Tarzına göre Shule seçkileri" },
];

export function TrustBadges() {
  const { t: __t } = __useLocale();
  return (
    <section data-testid="trust-badges-section" className="py-14 md:py-20 border-t border-shule-line">
      <div className="container-shule grid grid-cols-2 lg:grid-cols-4 gap-6">
        {TRUST.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.07} className="text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-shule-goldlight/50 border border-shule-gold/30 flex items-center justify-center">
              <t.icon className="w-6 h-6 text-shule-goldhover" />
            </div>
            <p className="mt-3 font-semibold text-sm">{__t(t.title)}</p>
            <p className="text-xs text-shule-soft mt-1">{__t(t.desc)}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
